import React, { useEffect, useRef, useState } from 'react';

const AIVisualization = () => {
    const canvasRef = useRef(null);
    const [currentAnimation, setCurrentAnimation] = useState(0);
    const [opacity, setOpacity] = useState(1);
    const totalAnimations = 5;

    useEffect(() => {
        const interval = setInterval(() => {
            setOpacity(0);
            setTimeout(() => {
                setCurrentAnimation((prev) => (prev + 1) % totalAnimations);
                setOpacity(1);
            }, 300);
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let animationFrameId;
        let nodes = [];
        let time = 0;

        const resizeCanvas = () => {
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
        };
        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        // 1. Neural Network
        const initNeural = () => {
            nodes = [];
            for (let i = 0; i < 18; i++) {
                nodes.push({
                    x: Math.random() * canvas.width,
                    y: Math.random() * canvas.height,
                    r: 3,
                    vx: (Math.random() - 0.5) * 0.4,
                    vy: (Math.random() - 0.5) * 0.4
                });
            }
        };

        const drawNeural = () => {
            ctx.fillStyle = 'rgba(11, 24, 44, 0.12)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            ctx.strokeStyle = 'rgba(100, 255, 147, 0.15)';
            ctx.lineWidth = 1;
            for (let i = 0; i < nodes.length; i++) {
                for (let j = i + 1; j < nodes.length; j++) {
                    const dx = nodes[i].x - nodes[j].x;
                    const dy = nodes[i].y - nodes[j].y;
                    if (dx * dx + dy * dy < 18000) {
                        ctx.beginPath();
                        ctx.moveTo(nodes[i].x, nodes[i].y);
                        ctx.lineTo(nodes[j].x, nodes[j].y);
                        ctx.stroke();
                    }
                }
            }

            ctx.fillStyle = '#64ff93';
            nodes.forEach(n => {
                ctx.beginPath();
                ctx.arc(n.x, n.y, n.r, 0, 6.28);
                ctx.fill();
                n.x += n.vx;
                n.y += n.vy;
                if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
                if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
            });
        };

        // 2. RAG Pipeline
        const initRAG = () => { time = 0; };

        const drawRAG = () => {
            ctx.fillStyle = 'rgba(11, 24, 44, 0.2)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            time += 0.025;

            const cy = canvas.height / 2;
            const x1 = 80, x2 = canvas.width / 2, x3 = canvas.width - 80;

            ctx.strokeStyle = '#64ff93';
            ctx.fillStyle = 'rgba(100, 255, 147, 0.1)';
            ctx.lineWidth = 2;

            // Query
            ctx.beginPath();
            ctx.roundRect(x1 - 40, cy - 35, 80, 70, 6);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = '#64ff93';
            ctx.font = '11px monospace';
            ctx.textAlign = 'center';
            ctx.fillText('Query', x1, cy + 5);

            // Vector DB
            ctx.fillStyle = 'rgba(100, 255, 147, 0.1)';
            ctx.beginPath();
            ctx.roundRect(x2 - 45, cy - 40, 90, 80, 6);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = 'rgba(100, 255, 147, 0.4)';
            ctx.fillRect(x2 - 30, cy - 22, 60, 7);
            ctx.fillRect(x2 - 30, cy - 8, 60, 7);
            ctx.fillRect(x2 - 30, cy + 6, 60, 7);
            ctx.fillStyle = '#64ff93';
            ctx.font = '10px monospace';
            ctx.fillText('Vector DB', x2, cy + 30);

            // LLM
            ctx.fillStyle = 'rgba(100, 255, 147, 0.1)';
            ctx.beginPath();
            ctx.roundRect(x3 - 40, cy - 35, 80, 70, 6);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = '#64ff93';
            ctx.font = '11px monospace';
            ctx.fillText('LLM', x3, cy + 5);

            // Lines
            ctx.strokeStyle = 'rgba(100, 255, 147, 0.3)';
            ctx.setLineDash([4, 4]);
            ctx.beginPath();
            ctx.moveTo(x1 + 45, cy);
            ctx.lineTo(x2 - 50, cy);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(x2 + 50, cy);
            ctx.lineTo(x3 - 45, cy);
            ctx.stroke();
            ctx.setLineDash([]);

            // Dots
            const p = (Math.sin(time * 2) + 1) / 2;
            ctx.fillStyle = '#64ff93';
            ctx.beginPath();
            ctx.arc(x1 + 50 + (x2 - x1 - 100) * p, cy, 5, 0, 6.28);
            ctx.fill();
            ctx.beginPath();
            ctx.arc(x2 + 55 + (x3 - x2 - 105) * p, cy, 5, 0, 6.28);
            ctx.fill();
        };

        // 3. Agentic Workflow
        const initAgent = () => { time = 0; };

        const drawAgent = () => {
            ctx.fillStyle = 'rgba(11, 24, 44, 0.2)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            time += 0.025;

            const cx = canvas.width / 2;
            const cy = canvas.height / 2;

            const tasks = [
                { x: cx, y: cy - 100, label: 'Plan' },
                { x: cx + 110, y: cy, label: 'Execute' },
                { x: cx, y: cy + 100, label: 'Review' },
                { x: cx - 110, y: cy, label: 'Search' }
            ];

            const active = Math.floor(time * 1.2) % 4;

            tasks.forEach((t, i) => {
                const next = tasks[(i + 1) % 4];
                ctx.strokeStyle = i === active ? 'rgba(100, 255, 147, 0.5)' : 'rgba(100, 255, 147, 0.15)';
                ctx.lineWidth = i === active ? 2 : 1;
                ctx.beginPath();
                ctx.moveTo(t.x, t.y);
                ctx.lineTo(next.x, next.y);
                ctx.stroke();
            });

            tasks.forEach((t, i) => {
                const isActive = i === active;
                ctx.fillStyle = isActive ? 'rgba(100, 255, 147, 0.2)' : 'rgba(100, 255, 147, 0.08)';
                ctx.strokeStyle = isActive ? '#64ff93' : 'rgba(100, 255, 147, 0.4)';
                ctx.lineWidth = isActive ? 2 : 1;
                ctx.beginPath();
                ctx.arc(t.x, t.y, 30, 0, 6.28);
                ctx.fill();
                ctx.stroke();
                ctx.fillStyle = isActive ? '#64ff93' : 'rgba(100, 255, 147, 0.6)';
                ctx.font = '10px monospace';
                ctx.textAlign = 'center';
                ctx.fillText(t.label, t.x, t.y + 4);
            });

            const from = tasks[active];
            const to = tasks[(active + 1) % 4];
            const p = (time * 1.2) % 1;
            ctx.fillStyle = '#64ff93';
            ctx.beginPath();
            ctx.arc(from.x + (to.x - from.x) * p, from.y + (to.y - from.y) * p, 5, 0, 6.28);
            ctx.fill();

            ctx.fillStyle = 'rgba(100, 255, 147, 0.15)';
            ctx.strokeStyle = '#64ff93';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.roundRect(cx - 30, cy - 18, 60, 36, 6);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = '#64ff93';
            ctx.font = '11px monospace';
            ctx.fillText('Agent', cx, cy + 5);
        };

        // 4. LLM Chat/Generation
        const initChat = () => { time = 0; };

        const drawChat = () => {
            ctx.fillStyle = 'rgba(11, 24, 44, 0.2)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            time += 0.03;

            const cx = canvas.width / 2;
            const cy = canvas.height / 2;

            // User message (left)
            ctx.fillStyle = 'rgba(100, 255, 147, 0.1)';
            ctx.strokeStyle = '#64ff93';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.roundRect(cx - 150, cy - 60, 120, 50, 8);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = 'rgba(100, 255, 147, 0.5)';
            ctx.fillRect(cx - 140, cy - 45, 80, 5);
            ctx.fillRect(cx - 140, cy - 32, 60, 5);
            ctx.fillStyle = '#64ff93';
            ctx.font = '9px monospace';
            ctx.textAlign = 'left';
            ctx.fillText('User', cx - 145, cy - 65);

            // AI response (right)
            ctx.fillStyle = 'rgba(100, 255, 147, 0.15)';
            ctx.beginPath();
            ctx.roundRect(cx + 30, cy + 10, 120, 70, 8);
            ctx.fill();
            ctx.stroke();

            // Typing animation
            const lines = Math.floor((time % 2) * 3);
            ctx.fillStyle = 'rgba(100, 255, 147, 0.5)';
            if (lines >= 1) ctx.fillRect(cx + 42, cy + 28, 96, 5);
            if (lines >= 2) ctx.fillRect(cx + 42, cy + 42, 75, 5);
            if (lines >= 3) ctx.fillRect(cx + 42, cy + 56, 88, 5);

            // Typing cursor
            if (lines < 3) {
                const cursorX = cx + 42 + (lines === 0 ? 0 : lines === 1 ? 96 : 75);
                const cursorY = cy + 28 + lines * 14;
                if (Math.sin(time * 8) > 0) {
                    ctx.fillStyle = '#64ff93';
                    ctx.fillRect(cursorX, cursorY, 8, 5);
                }
            }

            ctx.fillStyle = '#64ff93';
            ctx.font = '9px monospace';
            ctx.textAlign = 'left';
            ctx.fillText('AI', cx + 35, cy + 5);

            // Arrow between
            ctx.strokeStyle = 'rgba(100, 255, 147, 0.4)';
            ctx.setLineDash([3, 3]);
            ctx.beginPath();
            ctx.moveTo(cx - 25, cy - 20);
            ctx.lineTo(cx + 25, cy + 30);
            ctx.stroke();
            ctx.setLineDash([]);
        };

        // 5. n8n Workflow Automation
        const initWorkflow = () => { time = 0; };

        const drawWorkflow = () => {
            ctx.fillStyle = 'rgba(11, 24, 44, 0.2)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            time += 0.02;

            const cx = canvas.width / 2;
            const cy = canvas.height / 2;

            // Workflow nodes in a branching pattern
            const nodes = [
                { x: cx - 120, y: cy, label: 'Trigger' },
                { x: cx - 40, y: cy - 50, label: 'AI Agent' },
                { x: cx - 40, y: cy + 50, label: 'Search' },
                { x: cx + 50, y: cy, label: 'Process' },
                { x: cx + 130, y: cy, label: 'Output' }
            ];

            // Draw connections
            const connections = [
                [0, 1], [0, 2], [1, 3], [2, 3], [3, 4]
            ];

            connections.forEach(([from, to]) => {
                ctx.strokeStyle = 'rgba(100, 255, 147, 0.25)';
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(nodes[from].x + 30, nodes[from].y);
                ctx.lineTo(nodes[to].x - 30, nodes[to].y);
                ctx.stroke();
            });

            // Draw nodes
            const activeNode = Math.floor(time * 0.7) % 5;
            nodes.forEach((n, i) => {
                const isActive = i === activeNode;
                ctx.fillStyle = isActive ? 'rgba(100, 255, 147, 0.25)' : 'rgba(100, 255, 147, 0.08)';
                ctx.strokeStyle = isActive ? '#64ff93' : 'rgba(100, 255, 147, 0.4)';
                ctx.lineWidth = isActive ? 2 : 1;
                ctx.beginPath();
                ctx.roundRect(n.x - 30, n.y - 20, 60, 40, 6);
                ctx.fill();
                ctx.stroke();

                ctx.fillStyle = isActive ? '#64ff93' : 'rgba(100, 255, 147, 0.6)';
                ctx.font = '9px monospace';
                ctx.textAlign = 'center';
                ctx.fillText(n.label, n.x, n.y + 4);
            });

            // Animated data packets
            const packetProgress = (time * 0.7) % 1;
            const packetNode = Math.floor(time * 0.7) % 5;
            if (packetNode < 4) {
                const connMap = [[0, 1], [1, 3], [3, 4], [0, 2]][packetNode % 4];
                if (connMap) {
                    const from = nodes[connMap[0]];
                    const to = nodes[connMap[1]];
                    ctx.fillStyle = '#64ff93';
                    ctx.beginPath();
                    ctx.arc(
                        from.x + 30 + (to.x - from.x - 60) * packetProgress,
                        from.y + (to.y - from.y) * packetProgress,
                        4, 0, 6.28
                    );
                    ctx.fill();
                }
            }

            // n8n-style label
            ctx.fillStyle = 'rgba(100, 255, 147, 0.6)';
            ctx.font = '8px monospace';
            ctx.textAlign = 'center';
            ctx.fillText('workflow automation', cx, cy + 90);
        };

        const init = () => {
            time = 0;
            if (currentAnimation === 0) initNeural();
            else if (currentAnimation === 1) initRAG();
            else if (currentAnimation === 2) initAgent();
            else if (currentAnimation === 3) initChat();
            else initWorkflow();
        };

        const draw = () => {
            if (currentAnimation === 0) drawNeural();
            else if (currentAnimation === 1) drawRAG();
            else if (currentAnimation === 2) drawAgent();
            else if (currentAnimation === 3) drawChat();
            else drawWorkflow();
            animationFrameId = requestAnimationFrame(draw);
        };

        init();
        draw();

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            cancelAnimationFrame(animationFrameId);
        };
    }, [currentAnimation]);

    return (
        <div className="ai-visualization">
            <canvas
                ref={canvasRef}
                className="neural-canvas"
                style={{ opacity, transition: 'opacity 0.3s ease' }}
            />
        </div>
    );
};

export default AIVisualization;
